import { useEffect, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import TreePicker, { TreePickerNode } from '@/components/ui/TreePicker';
import { addressService } from '@/services/addressService';
import { PaginatedResponse } from '@/services/apiClient';
import { useTranslation } from '@/hooks/useTranslation';

export interface GeographicAddress { countryId: string; adminAreaId: string; localityId: string }
interface Props {
  value: GeographicAddress[]; onChange: (value: GeographicAddress[]) => void;
  disabled?: boolean; error?: string; singleSelect?: boolean; label?: string; help?: string;
}
const addressId = (value: GeographicAddress) => value.localityId ? 'locality:' + value.localityId : value.adminAreaId ? 'area:' + value.adminAreaId : value.countryId ? 'country:' + value.countryId : '';

// Fetch every page of an opened branch so larger regions are not silently truncated.
async function allPages<T>(fetchPage: (page: number) => Promise<PaginatedResponse<T>>) {
  const first = await fetchPage(0);
  const items = [...first.content];
  for (let page = 1; page < first.totalPages; page++) items.push(...(await fetchPage(page)).content);
  return items;
}

export default function GeographicLocationPicker({ value, onChange, disabled, error, singleSelect = false, label, help }: Props) {
  const { t: { audience: c } } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const searchResults = useQuery({
    queryKey: ['address', 'tree', 'search', debouncedSearch],
    enabled: Boolean(debouncedSearch),
    queryFn: async () => {
      const [countries, areas, localities] = await Promise.all([
        allPages(page => addressService.getCountries({ search: debouncedSearch, _page: page, _size: 200 })),
        allPages(page => addressService.getAdminAreas({ search: debouncedSearch, _page: page, _size: 200 })),
        allPages(page => addressService.getLocalities({ search: debouncedSearch, _page: page, _size: 200 })),
      ]);
      const missingAreaIds = [...new Set(localities.map(item => item.adminAreaId))].filter(id => !areas.some(area => area.id === id));
      const parents = await Promise.all(missingAreaIds.map(id => addressService.getAdminArea(id)));
      return { countries, areas: [...areas, ...parents], localities,
        matches: [...countries.map(item => 'country:' + item.id), ...areas.map(item => 'area:' + item.id), ...localities.map(item => 'locality:' + item.id)] };
    },
    staleTime: 600000,
  });
  const [opened, setOpened] = useState<string[]>([]);
  const countryIds = [...new Set([...opened.filter(id => id.startsWith('country:')).map(id => id.slice(8)), ...value.map(item => item.countryId)].filter(Boolean))];
  const areaIds = [...new Set([...opened.filter(id => id.startsWith('area:')).map(id => id.slice(5)), ...value.map(item => item.adminAreaId)].filter(Boolean))];
  const countries = useQuery({
    queryKey: ['address', 'tree', 'countries'],
    queryFn: () => allPages(page => addressService.getCountries({ _page: page, _size: 200 })),
    staleTime: 600000,
  });
  const areas = useQueries({ queries: countryIds.map(countryId => ({
    queryKey: ['address', 'tree', 'areas', countryId],
    queryFn: () => allPages(page => addressService.getAdminAreas({ countryId, _page: page, _size: 500 })),
    staleTime: 600000,
  })) });
  const localities = useQueries({ queries: areaIds.map(adminAreaId => ({
    queryKey: ['address', 'tree', 'localities', adminAreaId],
    queryFn: () => allPages(page => addressService.getLocalities({ adminAreaId, _page: page, _size: 500 })),
    staleTime: 600000,
  })) });
  const nodes: TreePickerNode[] = [];
  const addresses = new Map<string, GeographicAddress>();
  for (const country of countries.data ?? []) {
    const id = 'country:' + country.id;
    const query = areas[countryIds.indexOf(country.id)];
    nodes.push({ id, name: country.name, description: c.country, hasChildren: !query?.data || query.data.length > 0 });
    addresses.set(id, { countryId: country.id, adminAreaId: '', localityId: '' });
  }
  areas.forEach((query, index) => {
    const countryId = countryIds[index];
    const country = countries.data?.find(item => item.id === countryId);
    for (const area of query.data ?? []) {
      const id = 'area:' + area.id;
      const children = localities[areaIds.indexOf(area.id)];
      nodes.push({ id, parentId: 'country:' + countryId, name: area.name, description: country?.adminAreaLabel || c.area, hasChildren: !children?.data || children.data.length > 0 });
      addresses.set(id, { countryId, adminAreaId: area.id, localityId: '' });
      for (const locality of children?.data ?? []) {
        const childId = 'locality:' + locality.id;
        nodes.push({ id: childId, parentId: id, name: locality.name, description: country?.localityLabel || c.locality });
        addresses.set(childId, { countryId, adminAreaId: area.id, localityId: locality.id });
      }
    }
  });
  const remote = search.trim() === debouncedSearch ? searchResults.data : undefined;
  for (const area of remote?.areas ?? []) {
    const id = 'area:' + area.id;
    const country = countries.data?.find(item => item.id === area.countryId);
    if (!nodes.some(node => node.id === id)) nodes.push({ id, parentId: 'country:' + area.countryId, name: area.name, description: country?.adminAreaLabel || c.area, hasChildren: true });
    addresses.set(id, { countryId: area.countryId, adminAreaId: area.id, localityId: '' });
  }
  for (const locality of remote?.localities ?? []) {
    const parent = addresses.get('area:' + locality.adminAreaId);
    if (!parent) continue;
    const id = 'locality:' + locality.id;
    const country = countries.data?.find(item => item.id === parent.countryId);
    if (!nodes.some(node => node.id === id)) nodes.push({ id, parentId: 'area:' + locality.adminAreaId, name: locality.name, description: country?.localityLabel || c.locality });
    addresses.set(id, { ...parent, localityId: locality.id });
  }
  const queries = [countries, ...areas, ...localities, ...(search.trim() ? [searchResults] : [])];
  const selected = value.map(addressId).filter(Boolean);
  return <fieldset disabled={disabled} className="space-y-2">
    <h3 className="font-semibold text-gray-900">{label ?? c.geography}</h3>
    {help && <p className="text-xs text-gray-500">{help}</p>}
    <TreePicker nodes={nodes} label={label ?? c.geography} selected={selected}
      singleSelect={singleSelect} hideLabel pillTrigger help={c.addressAutocompleteHelp} emptyText={c.noAddressResults}
      onSearchChange={setSearch} searchMatchIds={search.trim() ? remote?.matches ?? [] : undefined}
      loading={search.trim() !== debouncedSearch || queries.some(query => query.isFetching)} loadError={queries.some(query => query.isError)}
      onRetry={() => { queries.filter(query => query.isError).forEach(query => { void query.refetch(); }); }}
      onExpand={id => setOpened(previous => previous.includes(id) ? previous : [...previous, id])}
      onChange={ids => {
        onChange(ids.flatMap(id => {
          const address = addresses.get(id) ?? value.find(item => addressId(item) === id);
          return address ? [address] : [];
        }));
      }} />
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
  </fieldset>;
}
